import React from 'react'
import clamp from 'lodash-es/clamp'

import T from 'components/FormattedMessage'
import Currency from 'components/Currency'
import ProgressBar from 'components/ProgressBar'

import Typography from '@material-ui/core/Typography'
import Grid, { GridProps } from '@material-ui/core/Grid'

import { AchievementTypeToken } from 'lib/enumTranslations'
import { Achievement, IAchievement } from 'protos'

import AchievementStatus from '../AchievementStatus'
import { WithStyles, createStyles, withStyles, Theme } from '@material-ui/core/styles'

interface Props extends GridProps {
  achievement: IAchievement
}

const coinLabelRenderer = (unlocked: number, total: number) => (
  <>
    <Currency value={unlocked} />
    {' / '}
    <Currency value={total} />
  </>
)

const pointLabelRenderer = (unlocked: number, total: number) => (
  <>
    {unlocked}{'pts'}
    {' / '}
    {total}{'pts'}
  </>
)

const labelRenderers = {
  [Achievement.Type.AT_CONTEST_WINNINGS]: coinLabelRenderer,
  [Achievement.Type.AT_BALANCE]: coinLabelRenderer,
  [Achievement.Type.AT_MATCH_SCORE]: pointLabelRenderer,
  [Achievement.Type.AT_PLAYER_SCORE]: pointLabelRenderer,
} as { [key: number]: typeof coinLabelRenderer }

const plainLabelRenderer = (unlocked: number, total: number) => `${unlocked}/${total}`

const styles = (theme: Theme) => createStyles({
  spacing: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

const AchievementListItem: React.SFC<InjectedProps> = ({ classes, achievement, ...props }) => {
  const numTotal = clamp(Number(achievement.nextLevelValue) | 0, 0, Infinity) // tslint:disable-line:no-bitwise
  const numUnlocked = clamp(Number(achievement.value) | 0, 0, numTotal) // tslint:disable-line:no-bitwise

  const progressedPercent = (numUnlocked / numTotal) * 100
  const isHighlighted = progressedPercent >= 90

  return (
    <Grid {...props} item>
      <Grid justify="center" container>
        <Grid xs={11} sm={8} item>
          <AchievementStatus level={achievement.level || 0} />
          <Typography className={classes.spacing} variant="body1" align="center">
            <T id={AchievementTypeToken(achievement.type!)} />
          </Typography>
          <ProgressBar
            min={0}
            max={numTotal}
            size="small"
            value={numUnlocked}
            color={isHighlighted ? 'secondary' : 'default'}
            label={(labelRenderers[achievement.type!] || plainLabelRenderer)(
              numUnlocked,
              numTotal
            )}
          />
          {achievement.currentLevelBonus && Boolean(achievement.currentLevelBonus.value) && (
            <Typography
              className={classes.spacing}
              color={isHighlighted ? 'textPrimary' : 'textSecondary'}
              variant="body2"
              align="center"
            >
              +<Currency {...achievement.currentLevelBonus} />
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default withStyles(styles)(AchievementListItem)
